import { exchangeGiftPaymentSession, fetchGiftPaymentOrder, PaymentApiError } from './api';
import { PaymentOrderView } from '../types';

const CSRF_STORAGE_PREFIX = 'gift-pay:csrf:';

export const getSessionTokenFromHash = (hash: string): string | null => {
    const fragment = hash.startsWith('#') ? hash.slice(1) : hash;
    return new URLSearchParams(fragment).get('session');
};

export const csrfStorageKey = (orderId: string): string => `${CSRF_STORAGE_PREFIX}${orderId}`;

export const saveCsrfToken = (orderId: string, token: string): void => {
    window.sessionStorage.setItem(csrfStorageKey(orderId), token);
};

export const getCsrfToken = (orderId: string): string | null =>
    window.sessionStorage.getItem(csrfStorageKey(orderId));

export const removeSessionFragment = (): void => {
    window.history.replaceState(null, document.title, `${window.location.pathname}${window.location.search}`);
};

/**
 * A URL session token is one-use. A 409 means it may already have produced the
 * cookie, so order GET is the source of truth for whether the browser can proceed.
 */
interface EstablishSessionOptions {
    /** Checkout needs the token later for confirm; read-only result pages only need the cookie. */
    requireCsrf?: boolean;
}

export const establishSessionAndFetchOrder = async (
    orderId: string,
    { requireCsrf = false }: EstablishSessionOptions = {},
): Promise<PaymentOrderView> => {
    const sessionToken = getSessionTokenFromHash(window.location.hash);

    if (sessionToken) {
        try {
            const exchanged = await exchangeGiftPaymentSession(orderId, sessionToken);
            if (exchanged.orderId !== orderId) {
                throw new Error('세션의 주문 정보가 일치하지 않아요.');
            }
            saveCsrfToken(orderId, exchanged.csrfToken);
            removeSessionFragment();
        } catch (error) {
            if (!(error instanceof PaymentApiError) || error.status !== 409) {
                throw error;
            }

            const order = await fetchGiftPaymentOrder(orderId);
            if (requireCsrf && !getCsrfToken(orderId)) {
                throw new Error('결제 승인 토큰을 찾을 수 없어요. 앱에서 결제를 다시 시작해 주세요.');
            }
            removeSessionFragment();
            return order;
        }
    }

    const order = await fetchGiftPaymentOrder(orderId);
    if (requireCsrf && !getCsrfToken(orderId)) {
        throw new Error('결제 승인 토큰을 찾을 수 없어요. 앱에서 결제를 다시 시작해 주세요.');
    }
    return order;
};
